import { LitElement, html, css } from 'lit'
import { StoreMixin } from '../store.js'
import { navigate } from '../router.js'
import { api } from '../api.js'
import { openPicker } from '../wmj-flow.js'
import { toast } from './app-toast.js'

// Client-organization detail + membership admin (§3.3, §6.3, §8.2, Phase 4).
// Agent-only. Manages the company's name, Workamajig link, email domains (the
// auto-onboarding trust signal), and individual members.
class PageClient extends StoreMixin(LitElement) {
  static properties = {
    params: { type: Array },
    _client: { state: true },
    _loading: { state: true },
    _notFound: { state: true },
    _name: { state: true },
    _domain: { state: true },
    _memberQuery: { state: true },
    _memberResults: { state: true },
    _subscription: { state: true }
  }

  static styles = css`
    :host { display: block; }
    .top { display: flex; align-items: baseline; gap: 0.75rem; margin-bottom: 1rem; }
    .back { cursor: pointer; color: var(--bx-brand); text-decoration: none; font-size: 0.9rem; }
    h1 { color: var(--bx-ink); margin: 0; font-size: 1.35rem; flex: 1; }
    .card { background: var(--bx-surface); border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
            box-shadow: var(--bx-shadow); padding: 1.1rem; margin-bottom: 1rem; }
    .card h2 { margin: 0 0 0.75rem; font-size: 1rem; color: var(--bx-ink); }
    .label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--bx-muted); margin-bottom: 0.3rem; }
    input { font: inherit; padding: 0.45rem 0.6rem; border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
            background: var(--bx-bg); color: var(--bx-ink); box-sizing: border-box; }
    .row { display: flex; gap: 0.5rem; align-items: center; }
    .row input { flex: 1; }
    button { font: inherit; cursor: pointer; padding: 0.45rem 0.8rem; border: 1px solid var(--bx-border);
             border-radius: var(--bx-radius); background: var(--bx-surface); color: var(--bx-ink); font-size: 0.85rem; }
    button.primary { background: var(--bx-accent); color: var(--bx-accent-ink); border-color: transparent; font-weight: 700; }
    button:disabled { opacity: 0.5; cursor: default; }
    .wmj { display: flex; align-items: center; gap: 0.6rem; font-size: 0.9rem; color: var(--bx-ink); }
    .warn { color: #b45309; font-size: 0.85rem; }
    ul { list-style: none; margin: 0 0 0.6rem; padding: 0; }
    li { display: flex; justify-content: space-between; align-items: center; padding: 0.35rem 0;
         border-bottom: 1px solid var(--bx-border); font-size: 0.9rem; }
    li:last-child { border-bottom: none; }
    li .sub { color: var(--bx-muted); font-size: 0.8rem; }
    li button { padding: 0.15rem 0.45rem; }
    .results { border: 1px solid var(--bx-border); border-radius: var(--bx-radius); margin-top: 0.3rem; }
    .results div { padding: 0.4rem 0.55rem; cursor: pointer; font-size: 0.88rem; }
    .results div:hover { background: var(--bx-bg); }
    .none { color: var(--bx-muted); font-size: 0.88rem; }
    .loading, .notfound { padding: 3rem; text-align: center; color: var(--bx-muted); }
  `

  constructor() {
    super()
    this._loading = true
    this._notFound = false
    this._name = ''
    this._domain = ''
    this._memberQuery = ''
    this._memberResults = []
  }

  connectedCallback() {
    super.connectedCallback()
    this._load()
  }

  get _id() { return this.params?.[0] }

  async _load() {
    if (!this._client) this._loading = true
    this._notFound = false
    try {
      this._client = await api.get(`/clients/${this._id}`)
      this._name = this._client.name
      // My standing subscription to this client (if any), for the toggle.
      const { subscriptions } = await api.get('/subscriptions')
      this._subscription = subscriptions.find(s => s.scope === 'client' && s.clientId === this._id) || null
    } catch (err) {
      if (err.statusCode === 404) this._notFound = true
      else toast(err.message || 'Failed to load client', 'error')
    } finally {
      this._loading = false
    }
  }

  async _rename() {
    const name = this._name.trim()
    if (!name || name === this._client.name) return
    try {
      await api.patch(`/clients/${this._id}`, { name })
      toast('Renamed', 'success')
      await this._load()
    } catch (err) { toast(err.message || 'Rename failed', 'error') }
  }

  async _linkWorkamajig() {
    try {
      const company = await openPicker({ mode: 'company' })
      if (!company) return
      await api.patch(`/clients/${this._id}`, { workamajigCompanyId: company.clientId })
      toast(`Linked to ${company.name}`, 'success')
      await this._load()
    } catch (err) { toast(err.message || 'Linking failed', 'error') }
  }

  async _addDomain() {
    const domain = this._domain.trim()
    if (!domain) return
    try {
      this._client = await api.post(`/clients/${this._id}/domains`, { domain })
      this._domain = ''
    } catch (err) { toast(err.message || 'Failed to add domain', 'error') }
  }

  async _removeDomain(domainId) {
    try {
      this._client = await api.delete(`/clients/${this._id}/domains/${domainId}`)
    } catch (err) { toast(err.message || 'Failed to remove domain', 'error') }
  }

  async _searchMember(e) {
    this._memberQuery = e.target.value
    if (this._memberQuery.trim().length < 2) { this._memberResults = []; return }
    try { this._memberResults = await api.get(`/users?query=${encodeURIComponent(this._memberQuery.trim())}`) }
    catch { this._memberResults = [] }
  }

  get _memberIsEmail() { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this._memberQuery.trim()) }

  async _addMember(body) {
    this._memberQuery = ''; this._memberResults = []
    try {
      this._client = await api.post(`/clients/${this._id}/users`, body)
      toast('Member added', 'success')
    } catch (err) { toast(err.message || 'Failed to add member', 'error') }
  }

  async _removeMember(userId) {
    try {
      this._client = await api.delete(`/clients/${this._id}/users/${userId}`)
    } catch (err) { toast(err.message || 'Failed to remove member', 'error') }
  }

  // Subscribe/unsubscribe the current agent to all of this client's ticket activity (§7.5).
  async _toggleSubscription() {
    try {
      if (this._subscription) {
        await api.delete(`/subscriptions/${this._subscription.id}`)
        this._subscription = null
        toast('Unsubscribed', 'success')
      } else {
        this._subscription = await api.post('/subscriptions', { scope: 'client', clientId: this._id })
        toast('Subscribed to this client', 'success')
      }
    } catch (err) { toast(err.message || 'Failed to update subscription', 'error') }
  }

  render() {
    if (this._loading) return html`<div class="loading">Loading…</div>`
    if (this._notFound) return html`<div class="notfound">Client not found.</div>`
    const c = this._client
    return html`
      <div class="top">
        <a class="back" @click=${() => navigate('/clients')}>← Clients</a>
        <h1>${c.name}</h1>
        <button class=${this._subscription ? '' : 'primary'} @click=${this._toggleSubscription}>
          ${this._subscription ? '✓ Subscribed' : 'Subscribe to activity'}
        </button>
      </div>

      <div class="card">
        <h2>Details</h2>
        <div class="label">Name</div>
        <div class="row">
          <input .value=${this._name} @input=${e => (this._name = e.target.value)} @keydown=${e => e.key === 'Enter' && this._rename()} />
          <button class="primary" ?disabled=${!this._name.trim() || this._name.trim() === c.name} @click=${this._rename}>Save</button>
        </div>
        <div class="label" style="margin-top:0.8rem;">Workamajig</div>
        <div class="wmj">
          ${c.workamajigCompanyId
            ? html`<span>Linked · <code>${c.workamajigCompanyId}</code></span><button @click=${this._linkWorkamajig}>Change…</button>`
            : html`<span class="warn">Not linked</span><button @click=${this._linkWorkamajig}>Link to Workamajig…</button>`}
        </div>
      </div>

      <div class="card">
        <h2>Domains</h2>
        <p class="none" style="margin-top:-0.4rem;">Senders on these domains auto-join this company.</p>
        ${c.domains.length ? html`<ul>
          ${c.domains.map(d => html`<li><span>${d.domain}</span><button @click=${() => this._removeDomain(d.id)}>Remove</button></li>`)}
        </ul>` : html`<p class="none">No domains yet.</p>`}
        <div class="row">
          <input placeholder="acme.com" .value=${this._domain}
            @input=${e => (this._domain = e.target.value)} @keydown=${e => e.key === 'Enter' && this._addDomain()} />
          <button class="primary" ?disabled=${!this._domain.trim()} @click=${this._addDomain}>Add domain</button>
        </div>
      </div>

      <div class="card">
        <h2>Members</h2>
        ${c.members.length ? html`<ul>
          ${c.members.map(m => html`<li>
            <span>${m.name || m.email}${m.name ? html` <span class="sub">${m.email}</span>` : ''}
              ${m.status !== 'active' ? html`<span class="sub"> · ${m.status}</span>` : ''}</span>
            <button @click=${() => this._removeMember(m.id)}>Remove</button>
          </li>`)}
        </ul>` : html`<p class="none">No members yet.</p>`}
        <input placeholder="Add member by name or email…" .value=${this._memberQuery} @input=${this._searchMember} />
        ${this._memberResults.length || this._memberIsEmail ? html`<div class="results">
          ${this._memberResults.map(u => html`<div @click=${() => this._addMember({ userId: u.id })}>${u.fullName || u.email} · ${u.role}</div>`)}
          ${this._memberIsEmail && !this._memberResults.some(u => (u.email || '').toLowerCase() === this._memberQuery.trim().toLowerCase())
            ? html`<div @click=${() => this._addMember({ email: this._memberQuery.trim() })}>Add “${this._memberQuery.trim()}”…</div>` : ''}
        </div>` : ''}
      </div>
    `
  }
}

customElements.define('page-client', PageClient)
